/**
 * ActivityCard Component
 * 
 * Displays activity information in a horizontal card format.
 * Used in HomeScreen's "Today's Activities" section.
 * 
 * Extracted from Figma: ORO - Nightfall Kit - ALL Pages
 * Reference: docs/design/FIGMA_IMPLEMENTATION_PLAN.md - Phase 7A
 */

import { Clock, MapPin, Users } from 'lucide-react';
import { useState } from 'react';
import { ActivityDateTime } from './ui/DateTimeDisplay';

export interface Activity {
  id: string;
  title: string;
  time: string; // Will be deprecated in favor of timestamp
  timestamp?: number; // Unix timestamp in seconds
  location: string;
  participants: number;
  imageUrl: string;
  status?: 'upcoming' | 'in-progress' | 'completed';
}

interface ActivityCardProps {
  activity: Activity;
  onClick?: () => void;
}

/**
 * Simple image component with fallback for broken images
 */
function ImageWithFallback({ 
  src, 
  alt, 
  className 
}: { 
  src: string; 
  alt: string; 
  className?: string;
}) {
  const [error, setError] = useState(false);

  if (error || !src) {
    return (
      <img 
        src="/images/event_fallback.jpg" 
        alt="Event fallback" 
        className={className}
      />
    );
  }

  return (
    <img 
      src={src} 
      alt={alt} 
      className={className}
      onError={() => setError(true)}
    />
  );
}

export default function ActivityCard({ activity, onClick }: ActivityCardProps) {
  return (
    <div
      className="flex gap-4 rounded-2xl p-4 cursor-pointer transition-colors"
      style={{
        backgroundColor: 'var(--oro-surface)',
        border: '1px solid var(--oro-border)',
        boxShadow: 'var(--shadow-card)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--oro-gold)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--oro-border)';
      }}
      onClick={onClick}
    >
      {/* Image */}
      <div className="shrink-0 w-20 h-20 rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--oro-surface-alt)' }}>
        <ImageWithFallback
          src={activity.imageUrl}
          alt={activity.title}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h3 className="text-base font-medium mb-2 truncate" style={{ color: 'var(--text-primary)' }}>
          {activity.title}
        </h3>

        <div className="space-y-1">
          <div className="flex items-center gap-2 text-[13px]" style={{ color: 'var(--text-muted)' }}>
            <Clock className="w-4 h-4 shrink-0" />
            {activity.timestamp ? (
              <ActivityDateTime 
                timestamp={activity.timestamp}
                className="text-[13px]"
              />
            ) : (
              <span className="truncate">{activity.time}</span>
            )}
          </div>

          <div className="flex items-center gap-2 text-[13px]" style={{ color: 'var(--text-muted)' }}>
            <MapPin className="w-4 h-4 shrink-0" />
            <span className="truncate">{activity.location}</span>
          </div>

          <div className="flex items-center gap-2 text-[13px]" style={{ color: 'var(--oro-gold)' }}>
            <Users className="w-4 h-4 shrink-0" />
            <span>{activity.participants} going</span>
          </div>
        </div>
      </div>
    </div>
  );
}
